import React, {useContext, useMemo} from 'react';
import {balanceOfFundsCosts} from "../../application-stages/_common/funding/BalanceOfFunds";
import useMortgage from "../../../../providers/mortgage/useMortgage";
import {InputNumber, Select, Space, Table} from "antd";
import {LenderTestFiguresContext} from "../../application-stages/_common/lender-tests/v4/LenderTestFiguresProvider";
import {getInitialLenderTestFigures} from "../../application-stages/_common/lender-tests/v4/LenderTestFiguresEditor";
import {funds} from "../../application-stages/_common/lender-tests/v4/figures-parts/funds";

export function getBalanceOfFundsInitialValues(mortgage, passTests) {
    const submissionTest = passTests.find(t => t?.tests?.items && t.tests.items.find(u => u.id === mortgage.activeSubmission?.verifiedFiguresTestID))
    const selectedResult = submissionTest?.tests?.items?.find(t => t.lenderCode === mortgage.activeSubmission?.lenderCode)
    const anyTestWithBof = submissionTest?.tests?.items?.find(t => t.bof)
    const bofRaw = anyTestWithBof && anyTestWithBof.bof
    const bof = bofRaw ? JSON.parse(bofRaw) : null

    const fnds = funds(mortgage)

    const getLoanAmount = () => {
        return {
            live: (() => {
                if (mortgage.activeSubmission?.finalLoan) {
                    return {
                        source: 'Loan Offer Finalised Loan Amount',
                        amount: mortgage.activeSubmission?.finalLoan
                    }
                }
                if (mortgage.activeSubmission?.aipMaxLoan) {
                    return {
                        source: 'AIP Maximum Loan Amount',
                        amount: mortgage.activeSubmission?.aipMaxLoan
                    }
                }
                if (mortgage.activeSubmission?.provisionalLoan) {
                    return {
                        source: 'Provisional Loan Amount',
                        amount: mortgage.activeSubmission?.provisionalLoan
                    }
                }
                return {
                    source: 'Stated Loan Required',
                    amount: mortgage.requirement.loanRequired || 0
                }
            })(),
            tested: selectedResult?.loan || 0
        }
    }
    const getPriceAmount = () => {
        return {
            live: (() => {
                if (mortgage.property.valuation2Value) {
                    return {
                        source: '2nd Valuation Value',
                        amount: mortgage.property.valuation2Value
                    }
                }
                if (mortgage.property.valuationValue) {
                    return {
                        source: 'Valuation Value',
                        amount: mortgage.property.valuationValue
                    }
                }
                if (mortgage.property.propertyPrice) {
                    return {
                        source: 'Sale Price',
                        amount: mortgage.property.propertyPrice
                    }
                }
                return {
                    source: 'Stated Property Value',
                    amount: mortgage.property.propertyValue || 0
                }
            })(),
            tested: selectedResult?.value || 0
        }
    }
    const getLegalFeesAmount = () => {
        const price = getPriceAmount()
        const liveCosts = balanceOfFundsCosts(mortgage, {price: price.live.amount})
        const testedCosts = balanceOfFundsCosts(mortgage, {price: price.tested})
        return {
            live: {
                source: 'Calculated',
                amount: liveCosts.legalFees
            },
            tested: testedCosts.legalFees
        }
    }
    const getStampDutyAmount = () => {
        const price = getPriceAmount()
        const liveCosts = balanceOfFundsCosts(mortgage, {price: price.live.amount})
        const testedCosts = balanceOfFundsCosts(mortgage, {price: price.tested})
        return {
            live: {
                source: 'Calculated',
                amount: liveCosts.stampDuty
            },
            tested: testedCosts.stampDuty
        }
    }


    const getGiftAmount = () => {
        const f = fnds.find(f => f.namesPath.includes('funds') && f.namesPath.includes('gifts'))
        return {
            live: {
                source: f?.source || 'None',
                amount: f?.value || 0,
            },
            tested: bof ? bof.funds.gifts || 0 : 0
        }
    }
    const getSavingsInBankAmount = () => {
        const f = fnds.find(f => f.namesPath.includes('funds') && f.namesPath.includes('savings'))
        return {
            live: {
                source: f.source,
                amount: f.value || 0,
                // part of amount held in investments (included in amount)
                investments: f.investmentsTotal || 0,
            },
            tested: bof ? bof.funds.savings || 0 : 0,
            muted: true
        }
    }
    const getHelpToBuyAmount = () => {
        const f = fnds.find(f => f.namesPath.includes('funds') && f.namesPath.includes('helpToBuy'))

        return {
            live: {
                source: f?.source,
                amount: f?.value || 0,
            },
            tested: bof ? bof.funds.helpToBuy || 0 : 0
        }
    }
    const getFirstHomeAmount = () => {
        const f = fnds.find(f => f.namesPath.includes('funds') && f.namesPath.includes('firstHome'))

        return {
            live: {
                source: f?.source,
                amount: f?.value ||0,
            },
            tested: bof ? bof.funds.firstHome || 0 : 0
        }
    }
    const getSellingPropertiesAmount = () => {
        const f = fnds.find(f => f.namesPath.includes('funds') && f.namesPath.includes('profitFromSale'))
        return {
            live: {
                source: f.source,
                amount: f.value,
            },
            tested: bof ? bof.funds.profitFromSale || 0 : 0
        }
    }
    const getContractDepositAmount = () => {
        const f = fnds.find(f => f.namesPath.includes('funds') && f.namesPath.includes('contractDepositPaid'))
        return {
            live: {
                source: f.source,
                amount: f.value,
            },
            tested: bof ? bof.funds.contractDepositPaid || 0 : 0
        }
    }
    const getBookingDepositAmount = () => {
        const f = fnds.find(f => f.namesPath.includes('funds') && f.namesPath.includes('bookingDepositPaid'))
        return {
            live: {
                source: f.source,
                amount: f.value,
            },
            tested: bof ? bof.funds.bookingDepositPaid || 0 : 0
        }
    }
    const getLoanClearanceAmount = () => {
        const f = fnds.find(f => f.namesPath.includes('funds') && f.namesPath.includes('loanClearance'))
        return {
            live: {
                source: f.source,
                amount: f.value  ,
            },
            tested: (bof ? bof.funds.loanClearance || 0 : 0)
        }
    }
    const getTotalCostsAmount = () => {
        const price = getPriceAmount()
        const liveCosts = balanceOfFundsCosts(mortgage, {price: price.live.amount})
        const testedCosts = balanceOfFundsCosts(mortgage, {price: price.tested})
        return {
            live: {
                source: 'Calculated',
                amount: liveCosts.totalCosts
            },
            tested: testedCosts.totalCosts
        }
    }
    const getTotalCostsAmountIncludingClearingLoans = () => {
        const totalCosts = getTotalCostsAmount()
        const loanClearance = getLoanClearanceAmount()
        console.log({loanClearance})
        return {
            live: {
                source: 'Calculated',
                amount: (totalCosts.live.amount || 0) + (loanClearance.live.amount || 0)
            },
            tested: (totalCosts.tested || 0) + (loanClearance.tested || 0)
        }
    }

    const result = {
        priceAmount: getPriceAmount(),
        legalFeesAmount: getLegalFeesAmount(),
        stampDutyAmount: getStampDutyAmount(),
        totalCostsAmount: getTotalCostsAmount(),
        totalCostsAmountIncludingClearingLoans: getTotalCostsAmountIncludingClearingLoans(),
        //--
        loanAmount: getLoanAmount(),
        giftAmount: getGiftAmount(),
        savingsAmount: getSavingsInBankAmount(),
        helpToBuyAmount: getHelpToBuyAmount(),
        firstHomeAmount: getFirstHomeAmount(),
        sellingPropertiesAmount: getSellingPropertiesAmount(),
        contractDepositAmount: getContractDepositAmount(),
        bookingDepositAmount: getBookingDepositAmount(),
        loanClearanceAmount: getLoanClearanceAmount(),
    }
    const savingsRequiredTested = result.totalCostsAmountIncludingClearingLoans.tested - (
        result.loanAmount.tested +
        result.giftAmount.tested +
        result.helpToBuyAmount.tested +
        result.firstHomeAmount.tested +
        result.sellingPropertiesAmount.tested +
        result.contractDepositAmount.tested +
        result.bookingDepositAmount.tested +
        result.loanClearanceAmount.tested
    )
    const savingsRequiredLive = result.totalCostsAmountIncludingClearingLoans.live.amount - (
        result.loanAmount.live.amount +
        result.giftAmount.live.amount +
        result.helpToBuyAmount.live.amount +
        result.firstHomeAmount.live.amount +
        result.sellingPropertiesAmount.live.amount +
        result.contractDepositAmount.live.amount +
        result.bookingDepositAmount.live.amount
    )
    result.savingsRequired = {
        live: {
            source: 'Calculated',
            amount: savingsRequiredLive ? Number(savingsRequiredLive.toFixed(2)) : 0
        },
        tested: savingsRequiredTested ? Number(savingsRequiredTested.toFixed(2)) : 0
    }


    const totalFunding = (() => {
        const totalFundingTested = result.loanAmount.tested +
            result.giftAmount.tested +
            result.savingsRequired.tested +
            result.helpToBuyAmount.tested +
            result.firstHomeAmount.tested +
            result.sellingPropertiesAmount.tested +
            result.contractDepositAmount.tested +
            result.bookingDepositAmount.tested -
            result.loanClearanceAmount.tested
        const totalFundingLive = result.loanAmount.live.amount +
            result.giftAmount.live.amount +
            result.savingsRequired.live.amount +
            result.helpToBuyAmount.live.amount +
            result.firstHomeAmount.live.amount +
            result.sellingPropertiesAmount.live.amount +
            result.contractDepositAmount.live.amount +
            result.bookingDepositAmount.live.amount -
            result.loanClearanceAmount.live.amount
        return {
            live: {
                source: 'Calculated',
                amount: totalFundingLive
            },
            tested: totalFundingTested
        }
    })()

    return {...result, totalFunding}
}

function BalanceOfFundsTable(props) {
    const mortgage = useMortgage()
    const {tests: {max, pass}} = useContext(LenderTestFiguresContext)
    //const figures = getInitialLenderTestFigures(mortgage)
    const passTests = pass.tests


    const columns = [
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
                align: 'left',
            render: (text, record) => {
                if (record.muted) {
                    return <span style={{color: 'rgba(0,0,0,0.25)'}}>{record.description}</span>
                }
                return record.description
            }
        },
        {
            title: 'Tested Amount',
            dataIndex: 'tested',
            key: 'tested',
            align: 'right',
            render: (text, record) => {
                if (record.muted) {
                    return <span style={{color: 'rgba(0,0,0,0.25)'}}>{record.tested}</span>
                }
                return record.tested
            }
        },
        {
            title: 'Live Amount',
            dataIndex: 'live.amount',
            key: 'live.amount',
            align: 'right',
            render: (text, record) => {
                if (record.muted) {
                    return <span style={{color: 'rgba(0,0,0,0.25)'}}>{record?.live?.amount}</span>
                }
                return record?.live?.amount
            }
        },
        {
            title: 'Source',
            dataIndex: 'source',
            key: 'source',
            align: 'left',
            render: (text, record) => {
                if (record.muted) {
                    return <span style={{color: 'rgba(0,0,0,0.25)'}}>{record?.live?.source}</span>
                }
                return record?.live?.source
            }
        },

    ]
    const IV = getBalanceOfFundsInitialValues(mortgage, passTests)
    console.log({IV})
    const costsDataSource = [
        {
            key: '1', description: 'Purchase Price', ...IV.priceAmount
        },
        {key: '2', description: 'Legal Fees', ...IV.legalFeesAmount},
        {key: '3', description: 'Stamp Duty', ...IV.stampDutyAmount},
        {key: '4', description: 'Total Costs', ...IV.totalCostsAmount},

    ]
    const fundsDataSource = [
        {key: '5', description: 'Loan Amount', ...IV.loanAmount},
        {key: '6', description: 'Gifts', ...IV.giftAmount},
        {key: '7', description: 'Help to Buy', ...IV.helpToBuyAmount},
        {key: '8', description: 'First Home Scheme', ...IV.firstHomeAmount},
        {key: '9', description: 'Booking Deposit Paid', ...IV.bookingDepositAmount},
        {key: '10', description: 'Selling Properties', ...IV.sellingPropertiesAmount},
        {key: '10.1', description: 'Loan(s) to clear before drawdown', ...IV.loanClearanceAmount},
        {key: '13', description: 'Required Savings', ...IV.savingsRequired},
        {key: '9', description: 'Actual Savings', ...IV.savingsAmount},
        {key: '11', description: 'Total funding', ...IV.totalFunding},
    ].filter(item => item.key === '10.1' || !!item.tested || !!item.live.amount)

    costsDataSource.push({
        key: 'z.ltv',
        description: 'LTV',
        tested: Number((IV.loanAmount.tested / IV.priceAmount.tested).toFixed(2)) || 'N/A',
        live: {
            amount: Number((IV.loanAmount.live.amount / IV.priceAmount.live.amount).toFixed(2)),
            source: 'Calculated'
        }
    })


    return (
        <div className="d-row j-equal gap-15">
            <Table size="small" dataSource={costsDataSource} columns={columns} pagination={false} rowKey="key"/>
            <Table size="small" dataSource={fundsDataSource} columns={columns} pagination={false} rowKey="key"/>
        </div>

    );
}

export default BalanceOfFundsTable;